/**
 * Maps an auth-chooser button's visible label (e.g. "Continue with Google")
 * to the analytics provider identifier used by `trackAuthProviderSelected`.
 *
 * The three provider-choice buttons rendered by `AuthExperience` (from
 * `@neuraville/react-core-pro`) don't expose a click callback to the host
 * app — Google/GitHub route through `onOAuthLogin`, but "Continue with
 * Neuraville" is purely an internal view-state change with no callback at
 * all. `SignupModal` observes all three via click-delegation on the
 * surrounding container and matches the rendered label against this
 * pattern. Kept in its own pure, dependency-free module so the matching
 * logic is unit-testable without rendering the third-party component.
 *
 * NOTE: this is deliberately coupled to the button copy produced by
 * `AuthExperience` (`${providerButtonPrefix} with <Provider>` — prefix is
 * either "Login" or "Continue", see the `providerButtonPrefix` prop). If
 * that copy ever changes, this matcher must be updated to match.
 */

export type NrsAuthProviderChoice = "google" | "github" | "neuraville";

const PROVIDER_BUTTON_LABEL_PATTERN =
  /^(?:Login|Continue) with (Google|GitHub|Neuraville)$/i;

/**
 * Returns the provider choice for a button's accessible label, or `null`
 * when the label does not match one of the three known auth-chooser
 * buttons (e.g. "Create one here", "Sign In", "Forgot Password?").
 */
export function resolveAuthProviderFromButtonLabel(
  label: string | null | undefined
): NrsAuthProviderChoice | null {
  if (!label) {
    return null;
  }
  const match = PROVIDER_BUTTON_LABEL_PATTERN.exec(label.trim());
  if (!match) {
    return null;
  }
  return match[1].toLowerCase() as NrsAuthProviderChoice;
}

/**
 * Coarse, PII-free auth-failure category for `trackAuthAttemptFailed` /
 * Composer's `auth_attempt_failed` funnel event. Categories are intentionally
 * limited so KPI breakdowns stay readable; raw error payloads and any
 * account-identifying details from `error.body` are never forwarded.
 *
 * Mirrors the classification `SignupModal`'s `isExpectedAuthFailure` already
 * uses to decide log severity, but every outcome here is tracked — an
 * "expected" wrong-password attempt is still real funnel drop-off.
 *
 * Kept in sync with `ALLOWED_AUTH_FAILURE_REASONS` in Composer's
 * `nrs_download_funnel_events.py` — update both together.
 */
export type NrsAuthFailureReason =
  | "invalid_credentials"
  | "account_conflict"
  | "consent_required"
  | "popup_closed_by_user"
  | "popup_blocked"
  | "oauth_cancelled"
  | "network_error"
  | "rate_limited"
  | "unknown_error";

export type NrsAuthFailureDiagnostics = {
  auth_status?: number;
  auth_code?: string;
  auth_detail?: string;
  auth_hint?: "invalid_credentials" | "network_error" | "popup" | "consent" | "rate_limited";
};

type AuthErrorLike = {
  status?: number;
  body?: {
    detail?: string;
    message?: string;
    code?: string;
    error?: { detail?: string; message?: string; code?: string; status?: number };
  };
  response?: {
    status?: number;
    data?: {
      detail?: string;
      message?: string;
      code?: string;
      status?: number;
      error?: { detail?: string; message?: string; code?: string; status?: number };
    };
  };
  data?: {
    detail?: string;
    message?: string;
    code?: string;
    status?: number;
    error?: { detail?: string; message?: string; code?: string; status?: number };
  };
  error?: { code?: string; message?: string; status?: number };
  code?: string;
  message?: string;
};

function pickFirstString(candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return "";
}

function normalizeAuthError(
  error: AuthErrorLike | null | undefined
): { status?: number; detail: string; code: string; message: string } {
  const statusCandidates = [
    error?.status,
    error?.response?.status,
    error?.error?.status,
    // Some backends nest status deeper in error wrappers.
    (error as { response?: { data?: { status?: number } } } | null | undefined)?.response?.data
      ?.status,
  ];
  const status = statusCandidates.find((value) => typeof value === "number");

  const detail = pickFirstString([
    error?.body?.detail,
    error?.response?.data?.detail,
    error?.data?.detail,
    (error as { response?: { data?: { error?: { detail?: string } } } } | null | undefined)
      ?.response?.data?.error?.detail,
    (error as { error?: { detail?: string } } | null | undefined)?.error?.detail,
    error?.body?.message,
    error?.response?.data?.message,
    error?.data?.message,
    (error as { response?: { data?: { error?: { message?: string } } } } | null | undefined)
      ?.response?.data?.error?.message,
    (error as { error?: { message?: string } } | null | undefined)?.error?.message,
  ])
    .trim()
    .toUpperCase();

  const code = pickFirstString([
    error?.code,
    error?.error?.code,
    (error as { response?: { data?: { code?: string } } } | null | undefined)?.response?.data
      ?.code,
    (error as { response?: { data?: { error?: { code?: string } } } } | null | undefined)
      ?.response?.data?.error?.code,
  ])
    .trim()
    .toLowerCase();

  const message = pickFirstString([
    error?.message,
    error?.error?.message,
    error?.body?.message,
    error?.response?.data?.message,
    error?.data?.message,
    (error as { response?: { data?: { error?: { message?: string } } } } | null | undefined)
      ?.response?.data?.error?.message,
  ])
    .trim()
    .toLowerCase();

  return { status, detail, code, message };
}

export function buildAuthFailureDiagnostics(
  error: AuthErrorLike | null | undefined
): NrsAuthFailureDiagnostics {
  const { status, detail, code, message } = normalizeAuthError(error);
  const diagnostics: NrsAuthFailureDiagnostics = {};
  if (typeof status === "number") {
    diagnostics.auth_status = status;
  }
  if (code) {
    diagnostics.auth_code = code.slice(0, 96);
  }
  if (detail) {
    diagnostics.auth_detail = detail.slice(0, 96);
  }

  if (
    detail === "INVALID_PASSWORD" ||
    detail === "EMAIL_NOT_FOUND" ||
    detail === "INVALID_CREDENTIALS" ||
    detail === "INVALID_LOGIN_CREDENTIALS" ||
    message.includes("invalid credential") ||
    message.includes("wrong password")
  ) {
    diagnostics.auth_hint = "invalid_credentials";
  } else if (
    code.includes("network-request-failed") ||
    message.includes("network error") ||
    message.includes("network request failed") ||
    message.includes("failed to fetch")
  ) {
    diagnostics.auth_hint = "network_error";
  } else if (code.includes("popup")) {
    diagnostics.auth_hint = "popup";
  } else if (status === 428 || detail === "CONSENT_REQUIRED") {
    diagnostics.auth_hint = "consent";
  } else if (status === 429 || detail === "TOO_MANY_REQUESTS") {
    diagnostics.auth_hint = "rate_limited";
  }

  return diagnostics;
}

export function classifyAuthFailureReason(
  error: AuthErrorLike | null | undefined
): NrsAuthFailureReason {
  const { status, detail, code, message } = normalizeAuthError(error);
  const detailLower = detail.toLowerCase();

  if (status === 401 || detail === "INVALID_PASSWORD" || detail === "EMAIL_NOT_FOUND") {
    return "invalid_credentials";
  }
  if (
    detail === "INVALID_CREDENTIALS" ||
    detail === "INVALID_LOGIN_CREDENTIALS" ||
    detailLower.includes("invalid credential") ||
    message.includes("invalid credential") ||
    message.includes("wrong password")
  ) {
    return "invalid_credentials";
  }
  if (
    status === 409 ||
    detail === "ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL" ||
    code.includes("account-exists-with-different-credential")
  ) {
    return "account_conflict";
  }
  if (
    status === 428 ||
    detail === "CONSENT_REQUIRED" ||
    message.includes("consent required")
  ) {
    return "consent_required";
  }
  if (status === 429 || detail === "TOO_MANY_REQUESTS" || code.includes("too-many-requests")) {
    return "rate_limited";
  }
  if (code.includes("popup-closed-by-user") || message.includes("popup closed by user")) {
    return "popup_closed_by_user";
  }
  if (code.includes("popup-blocked") || message.includes("popup blocked")) {
    return "popup_blocked";
  }
  if (
    code.includes("cancelled-popup-request") ||
    code.includes("operation-cancelled") ||
    message.includes("cancelled popup request")
  ) {
    return "oauth_cancelled";
  }
  if (
    status === 0 ||
    code.includes("network-request-failed") ||
    message.includes("network error") ||
    message.includes("network request failed") ||
    message.includes("failed to fetch") ||
    detailLower.includes("network")
  ) {
    return "network_error";
  }
  return "unknown_error";
}
